/**
 * Onboarding API helpers
 * Thin wrappers around axiosInstance for all onboarding step data operations.
 */
import axios from '../../utils/axiosInstance';

// ─── Site Branding ───────────────────────────────────────────────
export async function fetchSiteBranding() {
  const res = await axios.get('/api/site-branding');
  return res.data;
}

export async function updateSiteBranding(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/site-branding/${id}`, data);
  return res.data;
}

// ─── Themes ──────────────────────────────────────────────────────
export async function fetchUserThemes() {
  const res = await axios.get('/api/user-theme');
  return res.data;
}

export async function activateTheme(id: string) {
  const res = await axios.put(`/api/user-theme/${id}/activate`);
  return res.data;
}

// ─── Hero Section ────────────────────────────────────────────────
export async function fetchHeroSection() {
  const res = await axios.get('/api/hero-section');
  return res.data;
}

export async function createHeroSection(data: Record<string, unknown>) {
  const res = await axios.post('/api/hero-section', data);
  return res.data;
}

export async function updateHeroSection(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/hero-section/${id}`, data);
  return res.data;
}

// ─── About Section ───────────────────────────────────────────────
export async function fetchAboutSection() {
  const res = await axios.get('/api/about-section');
  return res.data;
}

export async function upsertAboutSection(data: Record<string, unknown>) {
  const res = await axios.post('/api/about-section', data);
  return res.data;
}

export async function updateAboutSection(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/about-section/${id}`, data);
  return res.data;
}

// ─── Showcase ────────────────────────────────────────────────────
export async function fetchShowcases() {
  const res = await axios.get('/api/showcase');
  return res.data;
}

export async function createShowcase(data: Record<string, unknown>) {
  const res = await axios.post('/api/showcase', data);
  return res.data;
}

export async function deleteShowcase(id: string) {
  const res = await axios.delete(`/api/showcase/${id}`);
  return res.data;
}

export async function reorderShowcases(items: { _id: string }[]) {
  const res = await axios.post('/api/showcase/reorder', { items });
  return res.data;
}

export async function updateShowcase(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/showcase/${id}`, data);
  return res.data;
}

export async function backfillShowcaseMetadata() {
  const res = await axios.post('/api/showcase/backfill-metadata');
  return res.data;
}

export async function fetchYouTubeDetails(videoId: string) {
  const res = await axios.get(`/api/youtube/video-details/${videoId}`);
  return res.data;
}

export interface YouTubeSearchResult {
  channelId: string;
  title: string;
  handle: string;
  thumbnail: string | null;
  subscriberCount: number | null;
  videoCount: number | null;
}

export async function searchYouTubeChannels(query: string, maxResults = 5): Promise<YouTubeSearchResult[]> {
  const res = await axios.get('/api/youtube/search', { params: { q: query, maxResults } });
  return res.data.results || [];
}

// ─── Categories ──────────────────────────────────────────────────
export async function fetchCategories(type: 'showcase' | 'image') {
  const res = await axios.get(`/api/category?type=${type}`);
  return res.data;
}

// ─── Images ──────────────────────────────────────────────────────
export async function uploadImageFile(file: Blob): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post('/api/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url;
}

export async function createImageRecord(data: Record<string, unknown>) {
  const res = await axios.post('/api/images', data);
  return res.data;
}

export async function fetchImages() {
  const res = await axios.get('/api/images');
  return res.data;
}

export async function deleteImage(id: string) {
  const res = await axios.delete(`/api/images/${id}`);
  return res.data;
}

export async function updateImage(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/images/${id}`, data);
  return res.data;
}

export async function reorderImages(items: { _id: string }[]) {
  const res = await axios.post('/api/images/reorder', { items });
  return res.data;
}

// ─── Platforms (Social Links) ────────────────────────────────────
export async function fetchMasterPlatforms() {
  const res = await axios.get('/api/master-platform');
  return res.data;
}

export async function fetchUserPlatforms(type: string) {
  const res = await axios.get(`/api/user-platforms?type=${type}`);
  return res.data;
}

export async function createUserPlatform(data: Record<string, unknown>) {
  const res = await axios.post('/api/user-platforms', data);
  return res.data;
}

export async function updateUserPlatform(id: string, data: Record<string, unknown>) {
  const res = await axios.put(`/api/user-platforms/${id}`, data);
  return res.data;
}

// ─── Tours & Events ─────────────────────────────────────────────
export async function createToursEvent(data: Record<string, unknown>) {
  const res = await axios.post('/api/tours-events', data);
  return res.data;
}

// ─── Awards ─────────────────────────────────────────────────────
export async function createAward(data: Record<string, unknown>) {
  const res = await axios.post('/api/awards-section', data);
  return res.data;
}
// ─── Identity Engine ─────────────────────────────────────────
export async function resolveYouTubeChannel(channelInput: string) {
  const res = await axios.post('/api/identity/youtube-resolve', { channelInput });
  return res.data;
}

export interface WikipediaPreview {
  title: string;
  url: string;
  description?: string;
  image?: string;
}

export async function previewWikipediaUrl(url: string, artistName?: string): Promise<WikipediaPreview | null> {
  const res = await axios.post('/api/identity/wikipedia-preview', { url, artistName });
  return res.data?.wikipedia ?? null;
}

export interface SpotifyPreview {
  artistId: string;
  name: string;
  url: string;
  image?: string;
  followers?: number | null;
  genres?: string[];
}

export async function previewSpotifyUrl(url: string): Promise<SpotifyPreview | null> {
  const res = await axios.post('/api/identity/spotify-preview', { url });
  return res.data?.spotify ?? null;
}

export async function buildIdentity(
  channelInput: string,
  sources: { wikipediaUrl?: string; spotifyUrl?: string; channelOnly?: boolean } = {},
) {
  const res = await axios.post('/api/identity/build', {
    input: channelInput,
    wikipediaUrl: sources.wikipediaUrl,
    spotifyUrl: sources.spotifyUrl,
    channelOnly: sources.channelOnly,
  });
  return res.data;
}

export async function pollBuildStatus() {
  const res = await axios.get('/api/identity/status');
  return res.data;
}
// ─── Onboarding Completion ──────────────────────────────────────
export async function completeOnboarding() {
  const res = await axios.put('/api/auth/complete-onboarding');
  return res.data;
}
